import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { checkpointApi } from '@/lib/checkpoint/api';

interface Props {
  /** 编辑模式：传入 suite id；新建模式：传入 undefined。 */
  suiteId?: string;
  /** 模板 yaml；新建时使用。 */
  initialYaml?: string;
  onClose: () => void;
  onSaved: (id: string) => void;
}

const NEW_YAML_TEMPLATE = `schemaVersion: 1
id: my-suite
name: 我的检查套件
description: 在这里写说明
appPackage: '*'
timeoutMs: 0
parameters: []
rules:
  - id: example
    name: 示例规则
    severity: pass
    matcher: keyword
    keywords:
      - 'TODO 把这里换成你想匹配的关键字'
`;

export function YamlEditDrawer({ suiteId, initialYaml, onClose, onSaved }: Props) {
  const [text, setText] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);
  const [savedId, setSavedId] = useState<string | undefined>(suiteId);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (suiteId) {
      setBusy(true);
      checkpointApi
        .getSuiteYaml(suiteId)
        .then((t) => {
          if (!cancelled) setText(t);
        })
        .catch((e) => {
          if (!cancelled) setErrors([e instanceof Error ? e.message : String(e)]);
        })
        .finally(() => {
          if (!cancelled) setBusy(false);
        });
    } else {
      setText(initialYaml ?? NEW_YAML_TEMPLATE);
    }
    return () => {
      cancelled = true;
    };
  }, [suiteId, initialYaml]);

  const validate = async (): Promise<boolean> => {
    setBusy(true);
    setErrors([]);
    try {
      const r = await checkpointApi.validateYaml(text);
      if (!r.valid) {
        setErrors(r.errors ?? ['unknown']);
        return false;
      }
      setSavedId(r.id);
      return true;
    } finally {
      setBusy(false);
    }
  };

  const save = async (): Promise<void> => {
    if (!(await validate())) return;
    setBusy(true);
    setErrors([]);
    try {
      const r = suiteId
        ? await checkpointApi.updateSuite(suiteId, text)
        : await checkpointApi.saveSuite(text);
      onSaved(r.id);
    } catch (e) {
      setErrors([e instanceof Error ? e.message : String(e)]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-stretch justify-end bg-black/40">
      <div className="flex h-full w-full max-w-3xl flex-col bg-bg-card shadow-2xl">
        <header className="flex items-center justify-between border-b border-line px-3 py-2">
          <h2 className="text-sm font-semibold">
            {suiteId ? `编辑套件 · ${suiteId}` : '新建套件'}
          </h2>
          <button onClick={onClose} className="text-ink-muted hover:text-ink">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 overflow-hidden p-3">
          <textarea
            className="h-full w-full resize-none rounded-md border border-line bg-bg-soft px-2 py-1.5 font-mono text-[12px] leading-relaxed text-ink focus:outline-none focus:ring-2 focus:ring-accent/40"
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
          />
        </div>
        {errors.length > 0 && (
          <div className="max-h-32 overflow-auto border-t border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-300">
            <p className="font-semibold">校验失败：</p>
            <ul className="ml-3 list-disc space-y-0.5">
              {errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}
        <footer className="flex items-center justify-between gap-2 border-t border-line px-3 py-2">
          <span className="text-[11px] text-ink-muted">
            {savedId ? `id=${savedId}` : '未保存'}
            {busy && '（处理中…）'}
          </span>
          <div className="flex gap-2">
            <button className="btn" onClick={() => void validate()} disabled={busy}>
              校验
            </button>
            <button className="btn-primary" onClick={() => void save()} disabled={busy}>
              保存
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
